import type { Scene, SceneGroup } from '@lumen5/luminary';

import Flags from 'js/src/libs/flags';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import { isVoiceDrivenSceneGroup } from 'js/src/luminary/luminary-selectors';

const sceneTimingCouldBeAffectedByTransition = (scene: Scene) => {
    if (!Flags.canUseTransitionsInVoicedrivenSceneGroups()) {
        return false;
    }
    if (!isVoiceDrivenSceneGroup(scene.sceneGroup)) {
        return false;
    }
    const prevScene = scene.prevScene;
    if (!prevScene) {
        return false;
    }
    if (prevScene.sceneGroup !== scene.sceneGroup) {
        return false;
    }
    return true;
};

export const getVoicedrivenSceneStartTime = (scene: Scene) => {
    let startTime = scene.sceneGroup.startTime;
    for (let i = 0; i < scene.sceneGroup.scenes.length; i++) {
        const currentScene = scene.sceneGroup.scenes[i];
        if (currentScene === scene) {
            return startTime;
        }
        startTime += getVoicedrivenSceneDuration(currentScene);
    }
    return startTime;
};

export const getVoicedrivenSceneEndTime = (scene: Scene) => {
    return getVoicedrivenSceneStartTime(scene) + getVoicedrivenSceneDuration(scene);
};

export const getVoicedrivenSceneDuration = (scene: Scene) => {
    if (!sceneTimingCouldBeAffectedByTransition(scene)) {
        return scene.duration;
    }
    const snapshot = _activeBatchSnapshots[scene.sceneGroup.id];
    if (snapshot) {
        const entry = snapshot[scene.id];
        if (!entry || entry.durationType === DurationType.LOGICAL_DURATION) {
            // Either this scene was added inside the active batch (no snapshot entry) or it was
            // written via setVoicedrivenSceneDuration. In both cases scene.duration currently
            // holds the logical (without-transition) duration; the adjust pass will add the
            // transition back at the end of the batch.
            return scene.duration;
        }
    }
    const transitionDuration = scene.calculateTransitionDuration();
    return scene.duration - transitionDuration;
};

/**
 * Writes scene.duration as a logical (without-transition) duration and marks the scene as having
 * been written inside the active runVoicedrivenBatchRunOperation. The post-batch adjust pass uses
 * this marker to decide whether to add the transition back.
 *
 */
export const setVoicedrivenSceneDuration = (scene: Scene, duration: number) => {
    scene.duration = duration;
    const snapshot = _activeBatchSnapshots[scene.sceneGroup.id];
    if (snapshot && snapshot[scene.id]) {
        snapshot[scene.id].durationType = DurationType.LOGICAL_DURATION;
    }
};

const _activeBatchSnapshots: Record<SceneGroupId, ScenesWithTransitionDuration> = {};

export enum DurationType {
    LOGICAL_DURATION = 'LOGICAL_DURATION',
    DURATION_INCLUDING_TRANSITION = 'DURATION_INCLUDING_TRANSITION',
}

type TransitionDuration = number;
type SceneId = string;
type SceneGroupId = string;
type ScenesWithTransitionDuration = Record<
    SceneId,
    {
        transition: TransitionDuration;
        durationType: DurationType;
    }
>;

const calculateScenesWithTransitionDurations = (
    sceneGroup: SceneGroup,
): ScenesWithTransitionDuration => {
    return sceneGroup.scenes.reduce((acc, scene) => {
        if (!sceneTimingCouldBeAffectedByTransition(scene)) {
            acc[scene.id] = {
                transition: 0,
                durationType: DurationType.DURATION_INCLUDING_TRANSITION,
            };
            return acc;
        }
        const transitionDuration = scene.calculateTransitionDuration();
        acc[scene.id] = {
            transition: transitionDuration,
            durationType: DurationType.DURATION_INCLUDING_TRANSITION,
        };
        return acc;
    }, {} as ScenesWithTransitionDuration);
};

const adjustSceneDurationsAfterBatchRunOperation = createLuminaryOperation(
    (
        { luminary },
        sceneGroupId: string,
        originalScenesWithTransitionDurations: ScenesWithTransitionDuration,
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }
        sceneGroup.scenes.forEach((scene) => {
            if (!sceneTimingCouldBeAffectedByTransition(scene)) {
                return;
            }
            const entry = originalScenesWithTransitionDurations[scene.id];
            const newTransitionDuration = scene.calculateTransitionDuration();
            if (!entry || entry.durationType === DurationType.LOGICAL_DURATION) {
                // The scene was added inside the batch (no snapshot entry) or was written via
                // setVoicedrivenSceneDuration. scene.duration currently holds the logical
                // duration — convert it to a duration that includes the transition.
                scene.duration = scene.duration + newTransitionDuration;
            } else {
                // The scene wasn't written inside the batch, so scene.duration still includes
                // the original transition. Things around the scene may have changed (eg. it was
                // moved or the preceding scene's duration changed), so swap the original
                // transition for the freshly-computed one.
                scene.duration = scene.duration - entry.transition + newTransitionDuration;
            }
        });
    },
);

/**
 * This function uses luminary.batchRun to make a bunch of changes to a voicedriven scene group.
 * During the batchRun, scenes can be treated as if they have no transitions - this simplifies
 * the mental model significantly. You should use:
 * - {@link getVoicedrivenSceneDuration}
 * - {@link setVoicedrivenSceneDuration}
 * - {@link getVoicedrivenSceneStartTime}
 * - {@link getVoicedrivenSceneEndTime}
 *
 * inside of the batchRun to read/write scene durations (as if there are no transitions).
 *
 * After the batchRun is complete, scene durations will be adjusted to add back the transitions.
 */
export const runVoicedrivenBatchRunOperation = createLuminaryOperation(
    ({ luminary, call }, sceneGroupId: string, cb: () => unknown) => {
        if (_activeBatchSnapshots[sceneGroupId]) {
            // Already inside a voicedriven batch for this same scene group. Reads and writes
            // operate on the existing snapshot directly; that batch's adjust pass will handle
            // reconciliation.
            return luminary.batchRun(cb);
        }
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        const originalScenesWithTransitionDurations = sceneGroup
            ? calculateScenesWithTransitionDurations(sceneGroup)
            : {};
        _activeBatchSnapshots[sceneGroupId] = originalScenesWithTransitionDurations;
        try {
            return luminary.batchRun(() => {
                const retVal = cb();
                call(
                    adjustSceneDurationsAfterBatchRunOperation,
                    sceneGroupId,
                    originalScenesWithTransitionDurations,
                );
                return retVal;
            });
        } finally {
            delete _activeBatchSnapshots[sceneGroupId];
        }
    },
);
